import React from "react";
import { Backdrop, Box, Paper, Stack, Typography } from "@mui/material";

export default function InterviewBackdrop({ open, variant }) {
  const isCompletion = variant === "completion";

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + (isCompletion ? 20 : 10),
        bgcolor: "rgba(15, 23, 42, 0.12)",
        backdropFilter: "blur(6px)",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          bgcolor: "#ffffff",
          border: "1px solid rgba(148, 163, 184, 0.35)",
          boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
          maxWidth: isCompletion ? 560 : 520,
          width: "calc(100% - 48px)",
        }}
      >
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Box
            sx={{
              width: 96,
              height: 96,
              borderRadius: 4,
              display: "grid",
              placeItems: "center",
              bgcolor: "transparent",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 88,
                height: 88,
                display: "grid",
                placeItems: "center",
                "--g": "#16a34a",
                "--gSoft": "rgba(22, 163, 74, 0.26)",
                "--g2": "rgba(22, 163, 74, 0.10)",
                background:
                  "conic-gradient(from 90deg, var(--g) 0 12deg, transparent 12deg 30deg, var(--g) 30deg 42deg, transparent 42deg 60deg, var(--g) 60deg 72deg, transparent 72deg 90deg, var(--g) 90deg 102deg, transparent 102deg 120deg, var(--g) 120deg 132deg, transparent 132deg 150deg, var(--g) 150deg 162deg, transparent 162deg 180deg, var(--g) 180deg 192deg, transparent 192deg 210deg, var(--g) 210deg 222deg, transparent 222deg 240deg, var(--g) 240deg 252deg, transparent 252deg 270deg, var(--g) 270deg 282deg, transparent 282deg 300deg, var(--g) 300deg 312deg, transparent 312deg 330deg, var(--g) 330deg 342deg, transparent 342deg 360deg)",
                borderRadius: "999px",
                boxShadow: "0 0 0 6px var(--g2) inset",
                animation: isCompletion ? "segSpin2 1.6s linear infinite" : "segSpin 1.6s linear infinite",
                "@keyframes segSpin": {
                  to: { transform: "rotate(360deg)" },
                },
                "@keyframes segSpin2": {
                  to: { transform: "rotate(360deg)" },
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 10,
                  borderRadius: "999px",
                  background: "#ffffff",
                  boxShadow: "0 0 0 1px rgba(22, 163, 74, 0.22) inset",
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  display: "inline-flex",
                  alignItems: "flex-end",
                  gap: 0.6,
                  height: 26,
                  "& i": {
                    display: "block",
                    width: 5,
                    borderRadius: 2,
                    background: "var(--g)",
                    boxShadow: "0 0 0 3px var(--gSoft)",
                    animation: isCompletion ? "barBounce2 1s ease-in-out infinite" : "barBounce 1s ease-in-out infinite",
                  },
                  "& i:nth-of-type(1)": { height: 16, animationDelay: "0s" },
                  "& i:nth-of-type(2)": { height: 22, animationDelay: "0.1s" },
                  "& i:nth-of-type(3)": { height: 18, animationDelay: "0.2s" },
                  "& i:nth-of-type(4)": { height: 24, animationDelay: "0.3s" },
                  "& i:nth-of-type(5)": { height: 16, animationDelay: "0.4s" },
                  "@keyframes barBounce": {
                    "0%, 100%": { transform: "scaleY(0.75)", opacity: 0.75 },
                    "50%": { transform: "scaleY(1.2)", opacity: 1 },
                  },
                  "@keyframes barBounce2": {
                    "0%, 100%": { transform: "scaleY(0.75)", opacity: 0.75 },
                    "50%": { transform: "scaleY(1.2)", opacity: 1 },
                  },
                }}
              >
                <i />
                <i />
                <i />
                <i />
                <i />
              </Box>
            </Box>
          </Box>

          {isCompletion ? (
            <Typography variant="h6" fontWeight={800}>
              Your interview is being submitted, please wait...
            </Typography>
          ) : (
            <>
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ color: "#0f172a" }}>
                  Your interview is being submitted
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(15, 23, 42, 0.7)", mt: 0.75 }}>
                  Please wait for sometime. Don&apos;t close or refresh this page.
                </Typography>
              </Box>

              <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: -0.5 }}>
                <Typography variant="caption" sx={{ color: "rgba(15, 23, 42, 0.6)", fontWeight: 600 }}>
                  Almost done
                </Typography>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.6,
                    pl: 0.4,
                    "& span": {
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      bgcolor: "primary.main",
                      opacity: 0.7,
                      animation: "dotBounce 1.1s ease-in-out infinite",
                    },
                    "& span:nth-of-type(2)": { animationDelay: "0.15s" },
                    "& span:nth-of-type(3)": { animationDelay: "0.3s" },
                    "@keyframes dotBounce": {
                      "0%, 80%, 100%": { transform: "translateY(0)", opacity: 0.55 },
                      "40%": { transform: "translateY(-5px)", opacity: 1 },
                    },
                  }}
                >
                  <span />
                  <span />
                  <span />
                </Box>
              </Stack>
            </>
          )}
        </Stack>
      </Paper>
    </Backdrop>
  );
}

